import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminOnboardingService } from '@/services/adminOnboarding.service';
import type {
  ApplicationFilter,
  CreateBeneficialOwnerDto,
  CreateInvitationDto,
  InvitationFilter,
  RejectApplicationDto,
  UpdateBeneficialOwnerDto,
} from '@/types/adminOnboarding';

/**
 * Admin Onboarding Query Hooks
 */

export const adminOnboardingKeys = {
  all: ['adminOnboarding'] as const,
  invitations: (filter?: InvitationFilter) =>
    [...adminOnboardingKeys.all, 'invitations', filter] as const,
  funnel: () => [...adminOnboardingKeys.all, 'funnel'] as const,
  beneficialOwners: () => [...adminOnboardingKeys.all, 'beneficialOwners'] as const,
  applications: (filter?: ApplicationFilter) =>
    [...adminOnboardingKeys.all, 'applications', filter] as const,
  applicationDetail: (userId: string) =>
    [...adminOnboardingKeys.all, 'applicationDetail', userId] as const,
};

// ─── Invitations ──────────────────────────────────────────────────────────────

export const useInvitations = (filter?: InvitationFilter) =>
  useQuery({
    queryKey: adminOnboardingKeys.invitations(filter),
    queryFn: () => adminOnboardingService.getInvitations(filter),
    staleTime: 2 * 60 * 1000,
  });

export const useCreateInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInvitationDto) => adminOnboardingService.createInvitation(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.all });
    },
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminOnboardingService.cancelInvitation(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.all });
    },
  });
};

// ─── Token Validation (public) ────────────────────────────────────────────────

export const useValidateToken = (token: string | null) =>
  useQuery({
    queryKey: [...adminOnboardingKeys.all, 'validateToken', token],
    queryFn: () => adminOnboardingService.validateToken(token!),
    enabled: !!token,
    retry: false,
    staleTime: 60 * 1000,
  });

// ─── Signup ───────────────────────────────────────────────────────────────────

export const useAdminSignup = () =>
  useMutation({
    mutationFn: (data: Parameters<typeof adminOnboardingService.signup>[0]) => adminOnboardingService.signup(data),
  });

// ─── Funnel ───────────────────────────────────────────────────────────────────

export const useFunnelState = () =>
  useQuery({
    queryKey: adminOnboardingKeys.funnel(),
    queryFn: () => adminOnboardingService.getFunnelState(),
    staleTime: 30 * 1000,
  });

export const useSubmitStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => adminOnboardingService.submitStep(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.funnel() });
    },
  });
};

export const useGoToStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stepOrder: number) => adminOnboardingService.goToStep(stepOrder),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.funnel() });
    },
  });
};

export const useUploadDocuments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepOrder, formData }: { stepOrder: number; formData: FormData }) =>
      adminOnboardingService.uploadDocuments(stepOrder, formData),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.funnel() });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => adminOnboardingService.deleteDocument(documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.funnel() });
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.beneficialOwners() });
    },
  });
};

// ─── Beneficial Owners ────────────────────────────────────────────────────────

export const useBeneficialOwners = () =>
  useQuery({
    queryKey: adminOnboardingKeys.beneficialOwners(),
    queryFn: () => adminOnboardingService.getBeneficialOwners(),
    staleTime: 30 * 1000,
  });

export const useCreateBeneficialOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBeneficialOwnerDto) =>
      adminOnboardingService.createBeneficialOwner(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.beneficialOwners() });
    },
  });
};

export const useUpdateBeneficialOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBeneficialOwnerDto }) =>
      adminOnboardingService.updateBeneficialOwner(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.beneficialOwners() });
    },
  });
};

export const useDeleteBeneficialOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminOnboardingService.deleteBeneficialOwner(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.beneficialOwners() });
    },
  });
};

export const useUploadBeneficialOwnerDocs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uboId, formData }: { uboId: string; formData: FormData }) =>
      adminOnboardingService.uploadBeneficialOwnerDocs(uboId, formData),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.beneficialOwners() });
    },
  });
};

// ─── Review (super admin) ─────────────────────────────────────────────────────

export const useApplications = (filter?: ApplicationFilter) =>
  useQuery({
    queryKey: adminOnboardingKeys.applications(filter),
    queryFn: () => adminOnboardingService.getApplications(filter),
    staleTime: 60 * 1000,
  });

export const useApplicationDetail = (userId: string | undefined) =>
  useQuery({
    queryKey: adminOnboardingKeys.applicationDetail(userId ?? ''),
    queryFn: () => adminOnboardingService.getApplicationDetail(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });

export const useApproveApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminOnboardingService.approveApplication(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.applications() });
    },
  });
};

export const useRejectApplication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: RejectApplicationDto }) =>
      adminOnboardingService.rejectApplication(userId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: adminOnboardingKeys.applications() });
      void queryClient.invalidateQueries({
        queryKey: adminOnboardingKeys.applicationDetail(variables.userId),
      });
    },
  });
};
